import { useState, useRef } from 'react';
import { Search, MapPin, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface MobileFullscreenSearchProps {
  onSearch: (query: string, coordinates?: [number, number]) => Promise<void>;
  placeholder?: string;
  onClose?: () => void;
  className?: string;
}

export function MobileFullscreenSearch({
  onSearch,
  placeholder = "Search destinations, cities...",
  onClose,
  className
}: MobileFullscreenSearchProps) {
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async () => {
    if (!searchValue.trim() || isSearching) return;

    setSearchError(false);
    try {
      setIsSearching(true);
      await onSearch(searchValue.trim());

      // Auto-close and blur input
      setTimeout(() => {
        onClose?.();
        inputRef.current?.blur();
      }, 500);
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
    if (e.key === 'Escape') {
      inputRef.current?.blur();
      onClose?.();
    }
  };

  const handleClear = () => {
    setSearchValue('');
    inputRef.current?.focus();
  };

  return (
    <motion.div
      className={cn("relative w-full max-w-sm mx-auto", className)}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={cn(
        "bg-white/96 backdrop-blur-xl rounded-xl shadow-2xl border border-white/60 transition-all duration-300",
        isFocused && "ring-2 ring-emerald-400/50",
        searchError && "ring-2 ring-red-400/60 border-red-200"
      )}>
      <div className="flex items-center">
        {/* Search Icon - Left */}
        <div className="absolute left-2.5 flex items-center justify-center w-4 h-4">
          {isSearching ? (
            <Loader2 className="h-3.5 w-3.5 text-emerald-600 animate-spin" />
          ) : (
            <MapPin className="h-3.5 w-3.5 text-emerald-600" />
          )}
        </div>

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={cn(
            "flex-1 bg-transparent border-none outline-none",
            "pl-8 pr-8 h-10",
            "text-sm text-gray-800 font-medium",
            "placeholder:text-gray-400 placeholder:text-sm",
            "touch-manipulation",
            "text-[16px]", // Prevent zoom on iOS
            "focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:outline-none rounded-l-xl"
          )}
          aria-label={placeholder || "Search locations"}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          inputMode="search"
        />

        {/* Clear Button */}
        <AnimatePresence>
          {searchValue && (
            <motion.button 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="absolute right-10 p-2.5 hover:bg-gray-100 rounded-full transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              onClick={handleClear}
              type="button"
              aria-label="Clear search"
            >
              <X className="h-3 w-3 text-gray-400" />
            </motion.button>
          )}
        </AnimatePresence>
      
        {/* Search Button - Compact */}
        <Button
          onClick={handleSearch}
          disabled={isSearching || !searchValue.trim()}
          className={cn(
            "h-10 w-10",
            "rounded-r-xl",
            "bg-gradient-to-r from-emerald-600 to-emerald-700",
            "hover:from-emerald-700 hover:to-emerald-800",
            "text-white shadow-lg hover:shadow-xl",
            "flex items-center justify-center",
            "transition-all duration-300",
            "touch-manipulation",
            "min-h-[44px] min-w-[44px]",
            "focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
            (isSearching || !searchValue.trim()) && "opacity-60 cursor-not-allowed"
          )}
          aria-label="Search"
        >
          <AnimatePresence mode="wait">
            {isSearching ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.2 }}
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              </motion.div>
            ) : (
              <motion.div
                key="search"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Search className="h-3.5 w-3.5" />
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
      </div>

      {/* Error message */}
      <AnimatePresence>
        {searchError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="px-3 py-2 text-xs text-red-600 border-t border-red-100 flex items-center gap-1.5"
          >
            <X className="h-3 w-3 shrink-0" />
            Location not found. Try a different search.
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      {/* Dismiss button — visible below the search card so users know they can close it */}
      {onClose && (
        <button
          onClick={onClose}
          className="mt-2 mx-auto flex items-center gap-1 text-xs text-white/80 hover:text-white transition-colors touch-manipulation min-h-[36px] px-3"
          aria-label="Close search"
        >
          <X className="h-3 w-3" />
          Close
        </button>
      )}
    </motion.div>
  );
} 